import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import AuthShell from './AuthShell';
import { ErrorNote, Field, Spinner } from './ui';

type Mode = 'signIn' | 'activate' | 'reset';

const errorKey = (code?: string): string => {
  switch (code) {
    case 'auth/invalid-credential':
    case 'auth/wrong-password':
    case 'auth/user-not-found':
    case 'auth/invalid-email':
      return 'auth.errors.invalidCredentials';
    case 'auth/not-invited':
      return 'auth.errors.notInvited';
    case 'auth/email-already-in-use':
      return 'auth.errors.emailInUse';
    case 'auth/weak-password':
      return 'auth.errors.weakPassword';
    case 'auth/too-many-requests':
      return 'auth.errors.tooManyRequests';
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return '';
    case 'auth/popup-blocked':
      return 'auth.errors.popupBlocked';
    case 'auth/network-request-failed':
      return 'auth.errors.network';
    default:
      return 'auth.errors.generic';
  }
};

const GoogleMark = () => (
  <svg className="h-5 w-5" viewBox="0 0 24 24" aria-hidden="true">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

const Login: React.FC = () => {
  const { signInWithGoogle, signInWithEmail, activateInvitation, sendPasswordReset } = useAuth();
  const { t } = useLanguage();
  const [mode, setMode] = useState<Mode>('signIn');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resetSent, setResetSent] = useState(false);

  const switchMode = (next: Mode) => {
    setMode(next);
    setError('');
    setResetSent(false);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      if (mode === 'signIn') {
        await signInWithEmail(email, password);
      } else if (mode === 'activate') {
        await activateInvitation(email, password, displayName);
        toast.success(t('auth.activated'));
      } else {
        await sendPasswordReset(email);
        setResetSent(true);
      }
    } catch (err: any) {
      // Never reveal whether an address exists when resetting a password.
      if (mode === 'reset') {
        setResetSent(true);
      } else {
        const key = errorKey(err?.code);
        if (key) setError(t(key));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError('');
    try {
      await signInWithGoogle();
    } catch (err: any) {
      const key = errorKey(err?.code);
      if (key) setError(t(key));
    }
  };

  const titles: Record<Mode, { title: string; text: string }> = {
    signIn: { title: t('auth.signInTitle'), text: t('auth.signInText') },
    activate: { title: t('auth.activateTitle'), text: t('auth.activateText') },
    reset: { title: t('auth.resetTitle'), text: t('auth.resetText') },
  };

  return (
    <AuthShell>
      <h1 className="text-2xl font-bold tracking-tight text-gray-900 dark:text-white">{titles[mode].title}</h1>
      <p className="mt-1.5 text-sm leading-relaxed text-gray-500 dark:text-gray-400">{titles[mode].text}</p>

      {mode === 'signIn' && (
        <>
          <button type="button" onClick={handleGoogle} className="btn btn-secondary mt-7 h-11 w-full">
            <GoogleMark />
            {t('auth.signInWithGoogle')}
          </button>
          <div className="my-6 flex items-center gap-3 text-xs text-gray-400">
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
            {t('auth.orWithEmail')}
            <span className="h-px flex-1 bg-gray-200 dark:bg-gray-800" />
          </div>
        </>
      )}

      {mode === 'reset' && resetSent ? (
        <div className="mt-7 space-y-5">
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-800 dark:border-emerald-900/60 dark:bg-emerald-950/40 dark:text-emerald-300">
            {t('auth.resetSent', { email })}
          </div>
          <button type="button" onClick={() => switchMode('signIn')} className="btn btn-secondary w-full">
            {t('auth.backToSignIn')}
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className={`space-y-4 ${mode === 'signIn' ? '' : 'mt-7'}`}>
          {mode === 'activate' && (
            <Field label={t('auth.name')} htmlFor="name">
              <input
                id="name"
                className="input"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                autoComplete="name"
              />
            </Field>
          )}
          <Field
            label={t('auth.email')}
            htmlFor="email"
            hint={mode === 'activate' ? t('auth.activateEmailHint') : undefined}
          >
            <input
              id="email"
              type="email"
              className="input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
            />
          </Field>
          {mode !== 'reset' && (
            <div>
              <div className="mb-1.5 flex items-baseline justify-between">
                <label htmlFor="password" className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  {mode === 'activate' ? t('auth.newPassword') : t('auth.password')}
                </label>
                {mode === 'signIn' && (
                  <button
                    type="button"
                    onClick={() => switchMode('reset')}
                    className="text-xs font-medium text-primary-600 hover:text-primary-700 dark:text-primary-400"
                  >
                    {t('auth.forgotPassword')}
                  </button>
                )}
              </div>
              <input
                id="password"
                type="password"
                className="input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === 'activate' ? 8 : undefined}
                autoComplete={mode === 'activate' ? 'new-password' : 'current-password'}
              />
              {mode === 'activate' && <p className="field-hint">{t('auth.passwordHint')}</p>}
            </div>
          )}

          {error && <ErrorNote>{error}</ErrorNote>}

          <button type="submit" disabled={loading} className="btn btn-primary h-11 w-full">
            {loading && <Spinner className="h-4 w-4" />}
            {mode === 'signIn' && t('auth.signIn')}
            {mode === 'activate' && t('auth.activateButton')}
            {mode === 'reset' && t('auth.sendResetLink')}
          </button>
        </form>
      )}

      <div className="mt-8 border-t border-gray-100 pt-5 text-sm text-gray-500 dark:border-gray-800 dark:text-gray-400">
        {mode === 'signIn' ? (
          <p>
            {t('auth.gotInvite')}{' '}
            <button
              type="button"
              onClick={() => switchMode('activate')}
              className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              {t('auth.activateLink')}
            </button>
          </p>
        ) : (
          <button
            type="button"
            onClick={() => switchMode('signIn')}
            className="font-semibold text-primary-600 hover:text-primary-700 dark:text-primary-400"
          >
            {t('auth.backToSignIn')}
          </button>
        )}
      </div>
    </AuthShell>
  );
};

export default Login;
